// export.ts — accountant-ready CSV builders (all local; nothing leaves the device).
// Formats match each tool's standard import layout so a customer can hand their books
// to QuickBooks/Xero (or their accountant) without re-keying.

import type { Expense } from "./store";

function cell(v: string | number): string {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function csv(headers: string[], rows: (string | number)[][]): string {
  return [headers, ...rows].map((r) => r.map(cell).join(",")).join("\n");
}
// date_on is YYYY-MM-DD. QBO defaults to US MM/DD/YYYY; Xero to intl DD/MM/YYYY.
function fmtDate(iso: string, style: "us" | "intl"): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return style === "us" ? `${m}/${d}/${y}` : `${d}/${m}/${y}`;
}

export type ExportFormat = "quickbooks" | "xero" | "generic";

/** QuickBooks Online 3-column bank/expense import: Date, Description, Amount (negative = money out). */
export function quickbooksCsv(exps: Expense[]): string {
  return csv(
    ["Date", "Description", "Amount"],
    exps.map((e) => [
      fmtDate(e.date_on, "us"),
      `${e.vendor}${e.category_name ? ` - ${e.category_name}` : ""}`,
      (-e.amount_cents / 100).toFixed(2),
    ]),
  );
}

/** Xero bank-statement import: Date, Amount, Payee, Description, Reference (negative = spend). */
export function xeroCsv(exps: Expense[]): string {
  return csv(
    ["Date", "Amount", "Payee", "Description", "Reference"],
    exps.map((e) => [
      fmtDate(e.date_on, "intl"),
      (-e.amount_cents / 100).toFixed(2),
      e.vendor,
      `${e.category_name ?? "Uncategorized"}${e.notes ? ` — ${e.notes}` : ""}`,
      e.id.slice(0, 8),
    ]),
  );
}

/** Full generic CSV — everything, for a spreadsheet or a custom import. */
export function genericCsv(exps: Expense[]): string {
  return csv(
    ["Date", "Vendor", "Amount", "Currency", "Category", "Status", "Notes"],
    exps.map((e) => [
      e.date_on, e.vendor, (e.amount_cents / 100).toFixed(2), e.currency, e.category_name ?? "", e.status, e.notes ?? "",
    ]),
  );
}

export function buildExport(format: ExportFormat, exps: Expense[]): { csv: string; filename: string } {
  const stamp = new Date().toISOString().slice(0, 10);
  if (format === "quickbooks") return { csv: quickbooksCsv(exps), filename: `expenses-quickbooks-${stamp}.csv` };
  if (format === "xero") return { csv: xeroCsv(exps), filename: `expenses-xero-${stamp}.csv` };
  return { csv: genericCsv(exps), filename: `expenses-${stamp}.csv` };
}
